import React, { useState, useEffect } from 'react';
import { TextStyle, type TextStyleAlign, Text as PixiTextClass } from 'pixi.js'; 
import { useExtend } from '@pixi/react'; 
import { API_BASE_URL } from '../constants'; // ADDED IMPORT

interface ScoreEntry {
  id: number;
  player_name: string;
  score: number;
  created_at: string;
}

const BASE_TEXT_STYLE_CONFIG = {
  fontFamily: '"Press Start 2P", Courier, monospace',
  fill: 0xffffff,
  stroke: { color: 0x000000, width: 2, join: 'round' as const },
  align: 'left' as TextStyleAlign,
};

const SCORE_TEXT_STYLE = new TextStyle({
  ...BASE_TEXT_STYLE_CONFIG,
  fontSize: 18,
});

const HEADER_TEXT_STYLE = new TextStyle({
  ...BASE_TEXT_STYLE_CONFIG, 
  fontSize: 22,              
  fill: 0xFFFF00,              
  align: 'center' as TextStyleAlign, 
  stroke: { color: 0x000000, width: 3, join: 'round' as const },
});

const FEEDBACK_TEXT_STYLE = new TextStyle({
    fontFamily: '"Press Start 2P", Courier, monospace',
    fontSize: 18, 
    fill: 0xffffff,
    stroke: { color: 0x000000, width: 3, join: 'round' as const },
    align: 'left' as TextStyleAlign, 
});

interface HighScoreDisplayComponentProps {
  x?: number;
  y?: number;
}

const HighScoreDisplayComponent: React.FC<HighScoreDisplayComponentProps> = ({ x = 0, y = 0 }) => {
  useExtend({ Text: PixiTextClass }); 

  const [scores, setScores] = useState<ScoreEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchScores = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // MODIFIED to use API_BASE_URL
        const response = await fetch(`${API_BASE_URL}/api/top-scores`); 
        if (!response.ok) {
          let errorDetails = `Failed to fetch scores. Status: ${response.status}`;
          try {
            const errorResult = await response.json();
            errorDetails = errorResult.error || errorResult.details || errorDetails;
          } catch (e) {
            errorDetails = `${errorDetails}. Server response: ${await response.text()}`;
          }
          throw new Error(errorDetails);
        }
        const data = await response.json();
        setScores(data);
      } catch (err) {
        console.error("Error fetching high scores:", err);
        setError((err as Error).message);
      }
      setIsLoading(false);
    };

    fetchScores();
  }, []); 

  const yOffset = 0; 
  const xOffset = 0; 
  const lineHeight = 28; 

  if (isLoading) {
    return <pixiText text="Loading High Scores..." x={xOffset} y={yOffset} style={FEEDBACK_TEXT_STYLE} anchor={{x:0, y:0}}/>;
  }

  if (error) {
    const errorStyle = FEEDBACK_TEXT_STYLE.clone(); 
    errorStyle.fill = 0xff0000; 
    return <pixiText text={`Error: ${error}`} x={xOffset} y={yOffset} style={errorStyle} anchor={{x:0, y:0}}/>;
  }

  return (
    <pixiContainer x={x} y={y}> 
      <pixiText text="Top Scores:" x={xOffset} y={yOffset} style={HEADER_TEXT_STYLE} anchor={{x:0, y:0}} />
      {scores.length === 0 && !isLoading && (
        <pixiText text="No scores yet!" x={xOffset} y={yOffset + lineHeight * 1.5} style={SCORE_TEXT_STYLE} anchor={{x:0, y:0}} />
      )}
      {scores.map((entry, index) => (
        <pixiText 
          key={entry.id} 
          text={`${index + 1}. ${entry.player_name} - ${entry.score}`}
          x={xOffset + 10} 
          y={yOffset + lineHeight * (index + 1.5)}
          style={SCORE_TEXT_STYLE}
          anchor={{x:0, y:0}}
        />
      ))}
    </pixiContainer>
  );
};

export default HighScoreDisplayComponent;
// Rewrite Count: 10
